import React, {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import debounce from 'lodash.debounce';

import NumberInput from 'src/common/components/NumberInput';
import { normalize } from 'src/common/components/NumberInput/helpers';

export type CapacityFilterProps = {
  inputNumRef?: React.Ref<HTMLInputElement>;
  value?: number;
  classNameModifier?: string;
  buttonsTabIndex?: number;
  autoFocus?: boolean;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => void;
  debounceDelay?: number;
};

const CapacityFilter: FC<CapacityFilterProps> = ({
  inputNumRef,
  value,
  classNameModifier = '',
  buttonsTabIndex,
  autoFocus = false,
  onChange,
  onBlur,
  onKeyDown,
  debounceDelay = 300,
}) => {
  const min = 0;
  const max = 10000;
  const [capacity, setCapacity] = useState<number>(value ?? 0);

  useEffect(() => setCapacity(normalize(value || 0, 0, max)), [value]);

  const memoizedDebouncedHandleChange = useMemo(
    () => debounce((v) => {
      onChange(v);
    }, debounceDelay),
    [debounceDelay, onChange],
  );

  const handleOnChange = useCallback(
    (v: number) => {
      setCapacity(v);
      memoizedDebouncedHandleChange(v);
    },
    [memoizedDebouncedHandleChange],
  );

  return (
    <NumberInput
      id="guest-count-filter"
      data-test="capacity-selector"
      inputNumRef={inputNumRef}
      min={min}
      max={max}
      value={capacity}
      onChange={handleOnChange}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onButtonKeyDown={onKeyDown}
      classNameModifier={classNameModifier}
      buttonsTabIndex={buttonsTabIndex}
      autoFocus={autoFocus}
    />
  );
};

export default CapacityFilter;
